import React, { useState, useEffect, useCallback } from 'react';
import api from '../utils/api';

const today = () => new Date().toISOString().split('T')[0];
const INIT = { project_id: '', task_id: '', user_id: '', date: today(), hours: '', description: '', billable: true, hourly_rate: '' };
const LIMIT = 15;
const fmt = (v) => parseFloat(v || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });

export default function TimeLogs({ setTitle }) {
  const [items, setItems]       = useState([]);
  const [total, setTotal]       = useState(0);
  const [summary, setSummary]   = useState({});
  const [page, setPage]         = useState(1);
  const [filterProject, setFilterProject] = useState('');
  const [filterInvoiced, setFilterInvoiced] = useState('');
  const [loading, setLoading]   = useState(false);
  const [modal, setModal]       = useState(false);
  const [form, setForm]         = useState(INIT);
  const [editId, setEditId]     = useState(null);
  const [saving, setSaving]     = useState(false);
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks]       = useState([]);
  const [users, setUsers]       = useState([]);

  useEffect(() => { setTitle('Time Logs'); }, [setTitle]);
  useEffect(() => {
    api.get('/projects/all').then(r => setProjects(r.data)).catch(() => {});
    api.get('/auth/users').then(r => setUsers(r.data)).catch(() => {});
  }, []);

  // Load tasks when project changes in form
  useEffect(() => {
    if (!form.project_id) { setTasks([]); return; }
    api.get(`/tasks?project_id=${form.project_id}&limit=200`).then(r => setTasks(r.data.data || [])).catch(() => {});
    // Auto-fill hourly rate from project
    const proj = projects.find(p => String(p.id) === String(form.project_id));
    if (proj && !editId) setForm(prev => ({ ...prev, hourly_rate: proj.hourly_rate || '' }));
  }, [form.project_id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: LIMIT });
      if (filterProject)  params.set('project_id', filterProject);
      if (filterInvoiced !== '') params.set('invoiced', filterInvoiced);
      const { data } = await api.get(`/timelogs?${params}`);
      setItems(data.data || []); setTotal(data.total || 0);
      setSummary(data.summary || {});
    } catch { setItems([]); } finally { setLoading(false); }
  }, [page, filterProject, filterInvoiced]);

  useEffect(() => { load(); }, [load]);

  const openAdd  = () => { setForm(INIT); setEditId(null); setModal(true); };
  const openEdit = (item) => {
    setForm({
      project_id: item.project_id || '', task_id: item.task_id || '',
      user_id: item.user_id || '', date: item.date?.split('T')[0] || today(),
      hours: item.hours, description: item.description || '',
      billable: !!item.billable, hourly_rate: item.hourly_rate || '',
    });
    setEditId(item.id); setModal(true);
  };
  const closeModal = () => setModal(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSave = async () => {
    if (!form.project_id) return alert('Please select a project');
    if (!form.hours || parseFloat(form.hours) <= 0) return alert('Hours must be greater than 0');
    setSaving(true);
    try {
      if (editId) await api.put(`/timelogs/${editId}`, form);
      else        await api.post('/timelogs', form);
      closeModal(); load();
    } catch (err) {
      alert(err.response?.data?.error || 'Error saving time log');
    } finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this time log?')) return;
    try { await api.delete(`/timelogs/${id}`); load(); }
    catch (err) { alert(err.response?.data?.error || 'Error deleting'); }
  };

  const totalPages = Math.ceil(total / LIMIT);

  return (
    <div className="page">
      <div className="page-header">
        <h1>Time Logs</h1>
        <button className="btn btn-primary" onClick={openAdd}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          Log Time
        </button>
      </div>

      {/* Summary Cards */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        {[
          { label: 'Total Hours', value: `${parseFloat(summary.total_hours || 0).toFixed(1)}h`, color: 'var(--primary)' },
          { label: 'Billable Amount', value: `$${fmt(summary.billable_amount)}`, color: 'var(--success)' },
          { label: 'Uninvoiced Amount', value: `$${fmt(summary.uninvoiced_amount)}`, color: 'var(--warning)' },
        ].map(card => (
          <div key={card.label} className="card" style={{ flex: '1 1 180px', padding: '14px 18px' }}>
            <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, color: 'var(--text-muted)', marginBottom: 4 }}>{card.label}</div>
            <div style={{ fontSize: 22, fontWeight: 700, color: card.color }}>{card.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="toolbar">
          <select className="form-control" style={{ width: 200 }} value={filterProject} onChange={e => { setFilterProject(e.target.value); setPage(1); }}>
            <option value="">All Projects</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
          <select className="form-control" style={{ width: 160 }} value={filterInvoiced} onChange={e => { setFilterInvoiced(e.target.value); setPage(1); }}>
            <option value="">All Entries</option>
            <option value="0">Uninvoiced</option>
            <option value="1">Invoiced</option>
          </select>
          <span className="text-muted" style={{ fontSize: 12, marginLeft: 'auto' }}>{total} entries</span>
        </div>

        <div className="table-wrap">
          {loading ? <div className="loading">Loading…</div> : (
            <table>
              <thead>
                <tr>
                  <th>#</th><th>Date</th><th>Project</th><th>Task</th><th>User</th>
                  <th>Hours</th><th>Rate</th><th>Amount</th><th>Billable</th><th>Invoiced</th><th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={11} className="empty">No time logs found</td></tr>
                ) : items.map((item, i) => (
                  <tr key={item.id}>
                    <td className="text-muted">{(page - 1) * LIMIT + i + 1}</td>
                    <td>{item.date?.split('T')[0]}</td>
                    <td><strong>{item.project_name || '—'}</strong></td>
                    <td className="text-muted">{item.task_title || '—'}</td>
                    <td>{item.user_name || '—'}</td>
                    <td><strong>{parseFloat(item.hours).toFixed(2)}h</strong></td>
                    <td className="text-muted">{item.hourly_rate > 0 ? `$${fmt(item.hourly_rate)}/hr` : '—'}</td>
                    <td><strong>${fmt(item.amount)}</strong></td>
                    <td>
                      <span className={`badge badge-${item.billable ? 'active' : 'inactive'}`}>
                        {item.billable ? 'Yes' : 'No'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge badge-${item.invoiced ? 'primary' : 'info'}`}>
                        {item.invoiced ? 'Invoiced' : 'Pending'}
                      </span>
                    </td>
                    <td>
                      <div className="actions">
                        {!item.invoiced && (
                          <button className="btn-icon" title="Edit" onClick={() => openEdit(item)}>
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                        )}
                        <button className="btn-icon danger" title="Delete" onClick={() => handleDelete(item.id)}>
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6M9 6V4h6v2"/></svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {totalPages > 1 && (
          <div className="pagination">
            <span>Showing {Math.min((page - 1) * LIMIT + 1, total)}–{Math.min(page * LIMIT, total)} of {total}</span>
            <div className="pagination-btns">
              <button className="page-btn" disabled={page === 1} onClick={() => setPage(p => p - 1)}>‹</button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                <button key={p} className={`page-btn ${p === page ? 'active' : ''}`} onClick={() => setPage(p)}>{p}</button>
              ))}
              <button className="page-btn" disabled={page === totalPages} onClick={() => setPage(p => p + 1)}>›</button>
            </div>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e => e.target === e.currentTarget && closeModal()}>
          <div className="modal">
            <div className="modal-header">
              <h2>{editId ? 'Edit Time Log' : 'Log Time'}</h2>
              <button className="modal-close" onClick={closeModal}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="form-grid">
                <div className="form-group">
                  <label>Project *</label>
                  <select className="form-control" value={form.project_id} onChange={e => f('project_id', e.target.value)}>
                    <option value="">— Select project —</option>
                    {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Task</label>
                  <select className="form-control" value={form.task_id} onChange={e => f('task_id', e.target.value)}>
                    <option value="">— No specific task —</option>
                    {tasks.map(t => <option key={t.id} value={t.id}>{t.title}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>User</label>
                  <select className="form-control" value={form.user_id} onChange={e => f('user_id', e.target.value)}>
                    <option value="">— Select user —</option>
                    {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date *</label>
                  <input type="date" className="form-control" value={form.date} onChange={e => f('date', e.target.value)} />
                </div>
                <div className="form-group">
                  <label>Hours *</label>
                  <input type="number" className="form-control" value={form.hours} onChange={e => f('hours', e.target.value)} placeholder="0.00" min="0.25" step="0.25" />
                </div>
                <div className="form-group">
                  <label>Hourly Rate ($)</label>
                  <input type="number" className="form-control" value={form.hourly_rate} onChange={e => f('hourly_rate', e.target.value)} placeholder="0.00" min="0" step="0.01" />
                </div>
                <div className="form-group full">
                  <label>Description</label>
                  <input className="form-control" value={form.description} onChange={e => f('description', e.target.value)} placeholder="What was worked on…" />
                </div>
                <div className="form-group full" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input type="checkbox" id="billable" checked={form.billable} onChange={e => f('billable', e.target.checked)} style={{ width: 16, height: 16 }} />
                  <label htmlFor="billable" style={{ margin: 0, cursor: 'pointer' }}>Billable to client</label>
                  {form.billable && form.hours && form.hourly_rate && (
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: 'var(--primary)' }}>
                      Amount: ${fmt(parseFloat(form.hours || 0) * parseFloat(form.hourly_rate || 0))}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-ghost" onClick={closeModal}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update' : 'Log Time'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
